import { ClipboardPaste, Download, FileUp, Info, Loader2 } from "lucide-react";
import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";

import { ProcessingOverlay } from "@/components/feedback/ProcessingOverlay";
import { Button } from "@/components/ui/button";
import { restoreFromBundle, restoreFromZip } from "@/lib/api";
import { downloadBlob } from "@/lib/download";
import type { FlatBundle, Slide } from "@/lib/types";
import { cn } from "@/lib/utils";
import { useBundleStore } from "@/stores/bundleStore";

type Phase = "idle" | "processing" | "done" | "error";
type InputMode = "file" | "paste";

function looksLikeSingleSlide(v: unknown): v is Slide {
  return (
    typeof v === "object" && v !== null && "slide_index" in v && "shapes" in v && !("manifest" in v)
  );
}

/**
 * When the user pastes a single-slide JSON (copied via "このスライドだけ"),
 * merge it into the bundle currently loaded in the app so a restore still
 * produces a complete .pptx. If there's no loaded bundle, surface a clear
 * error instead of the opaque "400".
 */
function resolveBundle(parsed: unknown, storedBundle: FlatBundle | null): FlatBundle {
  if (looksLikeSingleSlide(parsed)) {
    if (!storedBundle) {
      throw new Error(
        "これは単一スライドの JSON のようです。先にアプリ画面で元の .pptx をアップロードしてから、この画面でスライドだけを貼り付けてください。",
      );
    }
    const slideToMerge = parsed as Slide;
    const matched = storedBundle.structure.some((s) => s.slide_index === slideToMerge.slide_index);
    if (!matched) {
      throw new Error(
        `スライド ${slideToMerge.slide_index} は現在読み込まれている bundle に見つかりませんでした。元ファイルと合っているか確認してください。`,
      );
    }
    return {
      ...storedBundle,
      structure: storedBundle.structure.map((s) =>
        s.slide_index === slideToMerge.slide_index ? slideToMerge : s,
      ),
    };
  }
  return parsed as FlatBundle;
}

export function RestorePage() {
  const storedBundle = useBundleStore((s) => s.bundle);

  const [mode, setMode] = useState<InputMode>("file");
  const [phase, setPhase] = useState<Phase>("idle");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [resultBlob, setResultBlob] = useState<Blob | null>(null);
  const [resultName, setResultName] = useState<string>("restored.pptx");
  const [pasteText, setPasteText] = useState("");

  const restoreFromJsonText = async (text: string) => {
    setPhase("processing");
    setErrorMsg("");
    try {
      const parsed = JSON.parse(text);
      const bundle = resolveBundle(parsed, storedBundle);
      const blob = await restoreFromBundle(bundle);
      setResultBlob(blob);
      const sourceName: unknown = bundle?.manifest?.source?.filename;
      const name =
        typeof sourceName === "string"
          ? `${sourceName.replace(/\.pptx$/i, "")}-restored.pptx`
          : "pasted-restored.pptx";
      setResultName(name);
      setPhase("done");
    } catch (e) {
      setPhase("error");
      setErrorMsg(e instanceof Error ? e.message : String(e));
    }
  };

  const onDrop = useCallback(
    async (files: File[]) => {
      const file = files[0];
      if (!file) return;

      setPhase("processing");
      setErrorMsg("");
      try {
        let blob: Blob;
        if (file.name.endsWith(".zip") || file.type === "application/zip") {
          blob = await restoreFromZip(file);
        } else {
          const text = await file.text();
          const parsed = JSON.parse(text);
          const bundle = resolveBundle(parsed, storedBundle);
          blob = await restoreFromBundle(bundle);
        }
        setResultBlob(blob);
        setResultName(file.name.replace(/\.(json|zip)$/i, "-restored.pptx"));
        setPhase("done");
      } catch (e) {
        setPhase("error");
        setErrorMsg(e instanceof Error ? e.message : String(e));
      }
    },
    [storedBundle],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/json": [".json"],
      "application/zip": [".zip"],
    },
    maxFiles: 1,
  });

  const restart = () => {
    setPhase("idle");
    setResultBlob(null);
    setErrorMsg("");
    setPasteText("");
  };

  return (
    <section className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-12">
      {phase === "processing" && (
        <ProcessingOverlay message="復元しています…" detail="JSON から .pptx を組み立てています" />
      )}
      <h1 className="mb-3 font-display text-3xl font-medium sm:text-4xl">
        JSON から PowerPoint を復元
      </h1>
      <p className="mb-2 text-[var(--color-ink-muted)]">
        アプリで変換した JSON（もしくは分割パック ZIP）を、元の .pptx に戻します。
      </p>
      <p className="mb-8 text-sm text-[var(--color-ink-muted)]">
        例：アプリ画面で
        <code className="mx-1 rounded px-1 font-mono text-[var(--color-ink)]">structure のみ</code>
        をコピー → ChatGPT / Gemini に貼って 「タイトルを短くして」などと指示 → 返ってきた JSON
        をこの画面に貼り付け →
        <code className="mx-1 rounded px-1 font-mono text-[var(--color-ink)]">.pptx を作成</code>
        ボタンで新しい .pptx を取得、という流れでお試しいただけます。
      </p>

      {phase !== "done" && (
        <>
          {/* Mode tabs */}
          <div className="mb-4 inline-flex rounded-[10px] border border-[var(--color-border)] bg-[var(--color-surface)] p-1 text-sm">
            <button
              type="button"
              onClick={() => setMode("file")}
              className={cn(
                "rounded-[8px] px-3 py-1",
                mode === "file" && "bg-[var(--color-coral)] text-white",
              )}
            >
              <FileUp size={13} className="-mt-0.5 mr-1 inline" />
              ファイルをドロップ
            </button>
            <button
              type="button"
              onClick={() => setMode("paste")}
              className={cn(
                "rounded-[8px] px-3 py-1",
                mode === "paste" && "bg-[var(--color-coral)] text-white",
              )}
            >
              <ClipboardPaste size={13} className="-mt-0.5 mr-1 inline" />
              JSON を貼り付け
            </button>
          </div>

          {mode === "file" && (
            <div
              {...getRootProps()}
              className={cn(
                "cursor-pointer rounded-[14px] border-2 border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-8 text-center transition sm:p-12",
                isDragActive && "border-[var(--color-coral)] bg-[var(--color-coral-soft)]/20",
                phase === "processing" && "pointer-events-none opacity-60",
              )}
            >
              <input {...getInputProps()} />
              <FileUp size={36} className="mx-auto mb-3 text-[var(--color-ink-muted)] sm:mb-4" />
              <p className="mb-2 font-display text-lg sm:text-xl">
                {phase === "processing"
                  ? "復元中…"
                  : isDragActive
                    ? "ここにドロップ"
                    : ".json / .zip をドロップ"}
              </p>
              <p className="text-xs text-[var(--color-ink-muted)] sm:text-sm">
                またはクリックで選択
              </p>
            </div>
          )}

          {mode === "paste" && (
            <div className="flex flex-col gap-3 rounded-[14px] border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
              {storedBundle && (
                <div className="flex items-start gap-2 rounded-[10px] bg-[var(--color-surface-muted)] p-3 text-xs leading-relaxed text-[var(--color-ink-muted)]">
                  <Info size={13} className="mt-0.5 shrink-0 text-[var(--color-coral)]" />
                  <div>
                    現在アプリに読み込まれているのは{" "}
                    <code className="rounded px-1 font-mono text-[var(--color-ink)]">
                      {storedBundle.manifest.source.filename}
                    </code>{" "}
                    です。「このスライドだけ」でコピーした単一スライド JSON を貼り付けると、その 1
                    枚だけを差し替えた 状態で .pptx
                    を作成します。全体を差し替えたい場合は「全体をコピー」や「単一
                    JSON」を使ってください。
                  </div>
                </div>
              )}
              <label
                htmlFor="paste-json"
                className="text-xs font-medium uppercase tracking-wide text-[var(--color-ink-muted)]"
              >
                JSON をここに貼り付け（Ctrl+V / Cmd+V）
              </label>
              <textarea
                id="paste-json"
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                rows={10}
                placeholder='{"manifest": { ... }, "structure": [...], "assets": {...}}'
                className="rounded-[10px] border border-[var(--color-border)] bg-[var(--color-code-bg)] p-3 font-mono text-xs focus:border-[var(--color-coral)] focus:outline-none"
                disabled={phase === "processing"}
              />
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs text-[var(--color-ink-soft)]">
                  AI から返ってきた JSON を 1 ファイル分そのまま貼ってください。
                </p>
                <Button
                  size="md"
                  onClick={() => restoreFromJsonText(pasteText)}
                  disabled={phase === "processing" || !pasteText.trim()}
                >
                  {phase === "processing" ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      復元中…
                    </>
                  ) : (
                    <>
                      <Download size={14} />
                      .pptx を作成
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {phase === "done" && resultBlob && (
        <div className="flex flex-col gap-4 rounded-[14px] border border-[var(--color-border)] bg-[var(--color-surface)] p-6 text-center sm:p-8">
          <p className="font-display text-xl">復元完了</p>
          <Button
            size="lg"
            onClick={() => {
              downloadBlob(resultBlob, resultName);
            }}
            className="w-full sm:w-auto sm:self-center"
          >
            <Download size={16} />
            <span className="truncate">{resultName} をダウンロード</span>
          </Button>
          <Button variant="secondary" size="sm" onClick={restart}>
            別のファイルを復元
          </Button>
        </div>
      )}

      {phase === "error" && (
        <div className="mt-6 rounded-[10px] border border-[var(--color-coral-dark)] bg-[var(--color-coral-soft)]/40 p-4 text-sm text-[var(--color-coral-dark)]">
          復元に失敗しました: {errorMsg}
          <button type="button" onClick={restart} className="ml-3 underline">
            やり直す
          </button>
        </div>
      )}
    </section>
  );
}
